import * as React from 'react';
import { useEditor } from '@grapesjs/react';
import {
  mdiArrowULeftTop,
  mdiArrowURightTop,
  mdiBorderRadius,
  mdiFullscreen,
  mdiImage,
  mdiImageAlbum,
  mdiUpload,
  mdiXml,
} from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect, useState } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { styled } from '@mui/material/styles';
import { IconButton, Tooltip, Box } from '@mui/material';
import * as Animatable from 'react-native-animatable';

// Styled components for enhanced theming
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.15)',
  color: 'white',
  padding: 8,
  margin: '0 4px',
  borderRadius: 10,
  backdropFilter: 'blur(5px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.25)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.3)',
  },
}));

const ButtonContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  padding: '8px 0',
});
interface CommandButton {
  id: string;
  iconPath: string;
  options?: Record<string, any>;
  disabled?: () => boolean;
}

export default function TopbarButtons({
  className, type,
}: React.HTMLAttributes<HTMLDivElement> & { type?: string }) {
  const editor = useEditor();
  const [, setUpdateCounter] = useState(0);
  const { UndoManager, Commands, CodeManager } = editor;

  useEffect(() => {
    Commands.add('open-import-template', {
      run(editor, sender) {
        // sender && sender.set('active', 0); // turn off the button

        const modal = editor.Modal;
        const container = document.createElement('div');
        const textarea = document.createElement('textarea');
        textarea.style.width = '100%';
        textarea.style.height = '700px';

        const btn = document.createElement('button');
        btn.innerHTML = 'Import';
        btn.onclick = () => {
          const code = textarea.value;
          editor.setComponents(code.trim());
          modal.close();
        };

        container.appendChild(textarea);
        container.appendChild(btn);
        modal.setTitle('Import HTML');
        modal.setContent(container);
        modal.open();
      },
    });


    const cmdEvent = 'run stop';
    const updateEvent = 'update';
    const updateCounter = () => setUpdateCounter((value) => value + 1);
    const onCommand = (id: string) => {
      cmdButtons.find((btn) => btn.id === id) && updateCounter();
    };
    editor.on(cmdEvent, onCommand);
    editor.on(updateEvent, updateCounter);

    return () => {
      editor.off(cmdEvent, onCommand);
      editor.off(updateEvent, updateCounter);
    };
  }, []);

  const cmdButtons: CommandButton[] = [
    type !== 'image' && {
      id: 'open-import-template',
      iconPath: mdiUpload,
    },
    {
      id: 'core:open-assets',
      iconPath: mdiImage,
    },
    {
      id: 'core:component-outline',
      iconPath: mdiBorderRadius,
    },
    {
      id: 'core:fullscreen',
      iconPath: mdiFullscreen,
      options: { target: '#root' },
    },
    type !== 'image' && {
      id: 'core:open-code',
      iconPath: mdiXml,
    },
    {
      id: 'core:undo',
      iconPath: mdiArrowULeftTop,
      disabled: () => !UndoManager.hasUndo(),
    },
    {
      id: 'core:redo',
      iconPath: mdiArrowURightTop,
      disabled: () => !UndoManager.hasRedo(),
    },
  ].filter(Boolean);

  return (
    <Animatable.View animation="fadeInRight" style={{ flex: 1 }}>
      <ButtonContainer>
        {cmdButtons.map(({ id, iconPath, disabled, options = {} }) => {
          const isActive = Commands.isActive(id);
          const isDisabled = disabled?.();
          
          return (
            <Tooltip 
              key={id} 
              title={id.replace('core:', '').replace('-', ' ').toUpperCase()}
              arrow
            >
              <span>
                <StyledIconButton
                  onClick={() =>
                    isActive
                      ? Commands.stop(id)
                      : Commands.run(id, options)
                  }
                  disabled={isDisabled}
                  style={{
                    backgroundColor: isActive 
                      ? 'rgba(255,255,255,0.3)' 
                      : 'rgba(255,255,255,0.15)',
                    opacity: isDisabled ? 0.4 : 1,
                  }}
                >
                  <Icon 
                    path={iconPath} 
                    color="white" 
                    size={0.9} 
                  />
                </StyledIconButton>
              </span>
            </Tooltip>
          );
        })}
      </ButtonContainer>
    </Animatable.View>
  );
}
